import React, { useEffect, useState } from "react";
import {
  FormVersionEnum,
  PublicFormElementInput,
  PublicFormVersion,
  useEmailMarketingFormPrefillDataQuery,
  useFormQuery,
  useSubmitFormMutation,
} from "__generated__/graphqlUnauthenticated";
import { useQueryParam } from "helpers/queryParam/useQueryParam";
import { PreviewFormUI } from "./PreviewFormUI";
import { redirectToTargetUrl } from "./helpers/redirectToTargetUrl";

interface IPreviewFormProps {
  uid: string;
  formUserToken?: string;
}

export const PreviewForm: React.FC<IPreviewFormProps> = ({
  uid,
  formUserToken,
}) => {
  const [isDraft, ,] = useQueryParam("draft");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const { data, loading: formLoading } = useFormQuery({
    variables: {
      uid: uid || "",
      version: isDraft
        ? FormVersionEnum.DraftVersion
        : FormVersionEnum.PublishedVersion,
    },
    skip: !uid,
  });
  const { data: prefillData, loading: prefillLoading } =
    useEmailMarketingFormPrefillDataQuery({
      variables: {
        uid: uid || "",
        emsUserToken: formUserToken || "",
      },
      skip: !uid || !formUserToken,
    });
  const [doSubmitForm, { loading, data: submitData, error }] =
    useSubmitFormMutation();

  // Check for waitlist redirect when form loads
  useEffect(() => {
    const waitlistSettings = data?.form?.waitlistSettings;
    if (waitlistSettings?.redirectRequired && waitlistSettings?.redirectUrl) {
      redirectToTargetUrl({ redirectUrl: waitlistSettings.redirectUrl });
    }
  }, [data?.form?.waitlistSettings]);

  const onSubmit = (inputs: PublicFormElementInput[]) =>
    doSubmitForm({
      variables: {
        formId: uid || "",
        version: PublicFormVersion.Published,
        inputs,
        recaptchaResponse: recaptchaToken,
      },
    }).then((result) => {
      const failReasons = result.data?.submitForm?.failReasons || [];
      const waitlistSettings = result.data?.submitForm?.form?.waitlistSettings;

      // Only redirect if form submission failed and waitlist conditions are met
      const hasErrors = failReasons.length > 0;
      const shouldRedirect = !!waitlistSettings?.redirectRequired;

      if (hasErrors && shouldRedirect) {
        setTimeout(() => {
          redirectToTargetUrl({ redirectUrl: waitlistSettings.redirectUrl });
        }, 3000);
      }
      return { error: failReasons[0] };
    });
  return (
    <PreviewFormUI
      isExpired={!!data?.form?.expired}
      expirySettings={data?.form?.expirySettings}
      loadingFormInfo={formLoading || prefillLoading}
      onSubmit={onSubmit}
      loading={loading}
      formDetails={data?.form || null}
      prefill={prefillData?.emailMarketingFormPrefillData?.entries || []}
      isDraft={!!isDraft}
      errorMessage={
        error?.message || submitData?.submitForm?.failReasons[0] || ""
      }
      setRecaptchaToken={(token: string) => setRecaptchaToken(token)}
      ff_update_form_validation={data?.ff_update_form_validation || false}
    />
  );
};
